import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetRecoilState } from 'recoil';

import { teamApi } from '../api';
import { TEAM_ASSIGNED_TASKS_QUERY_KEY, TEAM_TASKS_QUERY_KEY } from '../queries';
import { eventsSnackBarAtom } from '../recoil';
import { translateI18n } from '../../i18n';

interface UpdateTeamTaskCompleteStatusParams {
  teamId: string;
  boardId: string;
  taskId: string;
  completed: boolean;
  showSnackbar?: boolean;
}

export const useUpdateTeamTaskCompleteStatus = () => {
  const queryClient = useQueryClient();
  const setEventsSnackbar = useSetRecoilState(eventsSnackBarAtom);

  const updateTeamTaskCompleteStatusMutation = useMutation({
    mutationFn: ({ teamId, boardId, taskId, completed }: UpdateTeamTaskCompleteStatusParams) => {
      return teamApi
        .updateCompleteStatusOfTeamTask(teamId, boardId, taskId, completed)
        .then(({ data }) => data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TEAM_ASSIGNED_TASKS_QUERY_KEY });

      if (variables.showSnackbar !== false) {
        setEventsSnackbar({
          message: variables.completed
            ? translateI18n('team.tasks.snackbar.completed')
            : translateI18n('team.tasks.snackbar.incompleted'),
          type: 'success',
          icon: 'task',
        });
      }
    },
    onError: (_error, variables) => {
      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.statusUpdateFailed'), type: 'error', icon: 'task' });
      }
    },
  });

  return {
    updateTeamTaskCompleteStatus: updateTeamTaskCompleteStatusMutation.mutateAsync,
    isUpdatingTeamTaskCompleteStatus: updateTeamTaskCompleteStatusMutation.isPending,
    ...updateTeamTaskCompleteStatusMutation,
  };
};
